import React, { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { Icon } from "@iconify/react";
import style from "../../../scss/Catalogos.module.scss";
import estiloPerfil from "../../../scss/Perfil.module.scss";
import BotonIconoComponent from "../../../components/BotonIconoComponent";
import { desactivarBiometria, getMisDispositivos } from "../../../Api/ApiBiometria";
import {
  activarBiometriaEnEsteEquipo,
  biometriaDisponible,
  leerCredencialLocal,
  mensajeErrorBiometria,
  quitarCredencialLocal,
} from "../../../utils/biometria";
import { obtenerMensajeError } from "../../../utils/mensajeError";

const formatearFecha = (fecha) =>
  fecha ? new Date(fecha).toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short" }) : "Nunca";

// Activa/desactiva el inicio de sesión con huella o reconocimiento facial.
// Cada dispositivo se activa por separado (la huella/rostro no sale del equipo).
const AccesoBiometricoComponent = () => {
  const [dispositivos, setDispositivos] = useState([]);
  const [disponible, setDisponible] = useState(false);
  const [credencialLocal, setCredencialLocal] = useState(leerCredencialLocal);
  const [procesando, setProcesando] = useState(false);

  const cargarDispositivos = useCallback(async () => {
    try {
      const lista = await getMisDispositivos();
      setDispositivos(lista);
      // si este equipo se desactivó desde otro dispositivo, se olvida aquí también
      const local = leerCredencialLocal();
      if (local && !lista.some((dispositivo) => dispositivo.credencialId === local)) {
        quitarCredencialLocal();
        setCredencialLocal(null);
      }
    } catch (error) {
      Swal.fire({ icon: "error", text: obtenerMensajeError(error) });
    }
  }, []);

  useEffect(() => {
    biometriaDisponible().then(setDisponible);
    cargarDispositivos();
  }, [cargarDispositivos]);

  const activoEnEsteEquipo = dispositivos.some((dispositivo) => dispositivo.credencialId === credencialLocal);

  const activar = async () => {
    setProcesando(true);
    try {
      const respuesta = await activarBiometriaEnEsteEquipo();
      setCredencialLocal(leerCredencialLocal());
      Swal.fire({ icon: "success", text: respuesta.msg, timer: 2200, showConfirmButton: false });
      cargarDispositivos();
    } catch (error) {
      Swal.fire({ icon: "error", text: mensajeErrorBiometria(error) });
    } finally {
      setProcesando(false);
    }
  };

  const confirmarDesactivar = (dispositivo) => {
    const esEsteEquipo = dispositivo.credencialId === credencialLocal;
    Swal.fire({
      title: `¿Desactivar el acceso con huella o rostro${esEsteEquipo ? " en este dispositivo" : ""}?`,
      text: `${dispositivo.dispositivo || "Dispositivo"}: tendrás que ingresar con usuario y contraseña. Puedes volver a activarlo cuando quieras.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, desactivar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const respuesta = await desactivarBiometria(dispositivo.id);
        if (esEsteEquipo) {
          quitarCredencialLocal();
          setCredencialLocal(null);
        }
        Swal.fire({ icon: "success", text: respuesta.msg, timer: 1800, showConfirmButton: false });
        cargarDispositivos();
      } catch (error) {
        Swal.fire({ icon: "error", text: obtenerMensajeError(error) });
      }
    });
  };

  return (
    <section className={style.tarjeta} aria-labelledby="titulo-acceso-biometrico">
      <h2 id="titulo-acceso-biometrico" className={style.titulo}>
        Huella o reconocimiento facial
      </h2>
      <p className={estiloPerfil.ayuda}>
        Inicia sesión sin escribir tu contraseña usando el lector de huella o el reconocimiento facial de tu dispositivo.
        Tu huella o tu rostro nunca salen del dispositivo.
      </p>

      {activoEnEsteEquipo ? (
        <p className={`${estiloPerfil.estado} ${estiloPerfil.estadoActivo}`}>
          <Icon icon="mdi:check-decagram" aria-hidden="true" />
          Activo en este dispositivo
        </p>
      ) : disponible ? (
        <button
          type="button"
          className={`btn btn-primary ${estiloPerfil.botonActivar}`}
          onClick={activar}
          disabled={procesando}
        >
          <Icon icon="mdi:fingerprint" aria-hidden="true" />
          {procesando ? "Esperando verificación..." : "Activar en este dispositivo"}
        </button>
      ) : (
        <p className={estiloPerfil.estado}>
          <Icon icon="mdi:information-outline" aria-hidden="true" />
          Este dispositivo o navegador no tiene lector de huella o reconocimiento facial disponible.
        </p>
      )}

      <h3 className={estiloPerfil.subtitulo}>
        Dispositivos activados <span className={style.contador}>{dispositivos.length}</span>
      </h3>

      {dispositivos.length === 0 ? (
        <p className={estiloPerfil.vacio}>Aún no has activado el acceso en ningún dispositivo.</p>
      ) : (
        <ul className={estiloPerfil.dispositivos}>
          {dispositivos.map((dispositivo) => (
            <li key={dispositivo.id} className={estiloPerfil.dispositivo}>
              <Icon icon="mdi:cellphone-key" className={estiloPerfil.dispositivoIcono} aria-hidden="true" />
              <div className={estiloPerfil.dispositivoDatos}>
                <strong>
                  {dispositivo.dispositivo || "Dispositivo"}
                  {dispositivo.credencialId === credencialLocal && (
                    <span className={style.chip}>Este dispositivo</span>
                  )}
                </strong>
                <small>
                  Activado: {formatearFecha(dispositivo.createdAt)} · Último uso: {formatearFecha(dispositivo.ultimoUso)}
                </small>
              </div>
              <BotonIconoComponent
                icono="mdi:fingerprint-off"
                etiqueta={`Desactivar ${dispositivo.dispositivo || "dispositivo"}`}
                variante="peligro"
                onClick={() => confirmarDesactivar(dispositivo)}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default AccesoBiometricoComponent;
